/**
 * «Me deben» — "¿quién me debe y hace cuánto?".
 *
 * Era la pantalla de Cuentas corrientes; ahora es la vista por defecto de
 * Clientes. Es la segunda pregunta mas frecuente del dia, despues de "¿cuánto
 * sale?". Por eso no es un listado neutro de saldos: esta ordenada por a quien
 * hay que llamar primero, y desde cada fila se cobra sin navegar a ningun lado.
 *
 * EL ORDEN NO ES POR IMPORTE
 * --------------------------
 * Ordenar por cuanto debe pondria arriba a un cliente grande que compro ayer, y
 * enterraria al que debe poco desde hace cuatro meses — que es justamente al que
 * hay que llamar. Se ordena por antiguedad de la mora, y dentro de cada tramo,
 * por importe.
 *
 * QUIEN APARECE
 * -------------
 * Todo el que tiene saldo, este en mora o no (`con_saldo=1`). El recorte de
 * alertas —solo mora y excedidos— es para el contador del header, no para
 * cobrar: una venta a cuenta de hoy esta al dia y no entra ahi, y el cliente
 * que venia a pagar al dia siguiente se encontraba con "Nadie te debe nada".
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Users, Wallet, type LucideIcon } from "lucide-react";

import { ListaDeudores, resumirDeudores } from "@/components/cobro/ListaDeudores";
import { ordenarDeudores, type DeudorFila } from "@/components/cobro/logica";
import { Card } from "@/components/ui/card";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Kpi } from "@/components/ui/kpi";
import { Importe } from "@/components/money/importe";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { useDeudas } from "@/lib/queries";
import { TOPE_FILAS, useVerMas } from "@/lib/usar-ver-mas";

/** Cuantos deudores se traen de una: el maximo que devuelve el backend. */
const POR_PAGINA = TOPE_FILAS;

export interface PanelDeudoresProps {
  onCobrar: (fila: DeudorFila) => void;
  /** Tocar el nombre de un deudor abre su ficha. */
  onAbrir: (clienteId: number) => void;
}

export function PanelDeudores({ onCobrar, onAbrir }: PanelDeudoresProps): JSX.Element {
  const [texto, setTexto] = useState("");

  /*
    LA BUSQUEDA VA A LA BASE.
    -------------------------
    Antes se filtraba en el navegador sobre los 500 deudores ya traidos: el dia
    que haya 501, buscar a alguien que existe y debe iba a dar "no lo encontre
    entre los que tienen saldo", que ademas es una mentira con forma de dato.
    Es el mismo bug que ya se arreglo en Ventas.
  */
  const busqueda = texto.trim();
  const { limite, verMas, reiniciar } = useVerMas(POR_PAGINA);
  useEffect(() => reiniciar(), [busqueda, reiniciar]);

  // `isLoading` es solo la primera carga; despues de un cobro la lista se
  // atenua con `isFetching` y nunca se vacia para volver a llenarse.
  const {
    data: deudas,
    isLoading,
    isFetching,
  } = useDeudas({ con_saldo: "1", limite, ...(busqueda ? { buscar: busqueda } : {}) });

  const filas = useMemo(() => ordenarDeudores(deudas?.filas ?? []), [deudas]);
  const total = deudas?.total ?? 0;
  // Los numeros de arriba salen de las filas traidas. Mientras haya mas en la
  // base que en pantalla son un parcial, y lo dicen: un total que se queda
  // corto sin avisar es peor que no tenerlo.
  const recortado = filas.length < total;

  /**
   * Los totales por moneda de anclaje van en el pie de la tabla (06 §3.5).
   * Arriba quedan los que no salen de sumar una columna: el equivalente al
   * dolar de hoy (mezcla, y por eso dice a que cotizacion) y los contadores.
   */
  const totales = useMemo(() => resumirDeudores(filas), [filas]);

  return (
    <>
      {/* ── Resumen ── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi
          icono={Wallet}
          etiqueta="Te deben hoy"
          monto={totales.debenHoy}
          moneda="ARS"
          tamano="md"
          nota={
            <>
              {recortado
                ? `de los ${filas.length} que se muestran, al dólar de hoy`
                : "convertido al dólar de hoy"}
              {/* Lo que el local le debe a sus clientes no se resta de lo que le
                  deben a el: va aparte, dicho. */}
              {totales.conAFavor > 0 ? (
                <>
                  . Aparte,{" "}
                  {totales.conAFavor === 1
                    ? "1 cliente tiene"
                    : `${totales.conAFavor} clientes tienen`}{" "}
                  <Importe monto={totales.aFavorHoy} moneda="ARS" tamano="sm" codigo={false} /> a
                  favor.
                </>
              ) : null}
            </>
          }
        />
        <Cifra
          icono={Users}
          etiqueta={total === 1 ? "Cliente con saldo" : "Clientes con saldo"}
          valor={total}
          nota={
            !recortado && totales.conAFavor > 0
              ? `${totales.deudores} ${totales.deudores === 1 ? "debe" : "deben"} · ${totales.conAFavor} con saldo a favor`
              : undefined
          }
        />
        <Cifra
          icono={AlertTriangle}
          etiqueta="En mora"
          valor={totales.enMora}
          alerta={totales.enMora > 0}
          nota={recortado ? `de los ${filas.length} que se muestran` : undefined}
        />
      </div>

      {/* ── Buscador ── */}
      <InputBusqueda
        valor={texto}
        onValorChange={setTexto}
        etiqueta="Buscar cliente"
        placeholder="Buscá un cliente"
        className="sm:max-w-xs"
      />

      {/* ── Lista ── */}
      <ListaDeudores
        filas={filas}
        onCobrar={onCobrar}
        onAbrir={onAbrir}
        cargando={isLoading}
        actualizando={isFetching && !isLoading}
        mensajeVacio={
          busqueda
            ? `No encontré a «${busqueda}» entre los que tienen saldo.`
            : "Nadie te debe nada."
        }
        onLimpiarFiltros={busqueda ? () => setTexto("") : undefined}
        pie={
          <PieDeLista
            visibles={filas.length}
            total={total}
            sustantivo="clientes con saldo"
            onVerMas={verMas}
            cargando={isFetching}
          />
        }
      />
    </>
  );
}

/**
 * Un contador con nombre (clientes, en mora): la misma tarjeta que `Kpi` pero
 * sin moneda, porque no es plata y no lleva ni chip ni color. `alerta` tiñe la
 * tarjeta, no el numero.
 */
function Cifra({
  icono,
  etiqueta,
  valor,
  alerta = false,
  nota,
}: {
  icono: LucideIcon;
  etiqueta: string;
  valor: number;
  alerta?: boolean;
  /** Que mide el numero cuando no mide todo. Misma idea que la `nota` del Kpi. */
  nota?: string | undefined;
}): JSX.Element {
  return (
    <Card
      as="section"
      padding="compacto"
      tono={alerta ? "warning" : "none"}
      className="flex flex-col gap-1"
    >
      <TituloSeccion nivel="etiqueta" icono={icono}>
        {etiqueta}
      </TituloSeccion>
      <span className="text-lg font-semibold tabular-nums">{valor}</span>
      {nota ? <span className="text-xs text-muted-foreground">{nota}</span> : null}
    </Card>
  );
}
